import type { GuidePage } from '../guides';

const example = [
  { item: 'Expected visits this season (your local average of plowable storms), times minutes per visit for this driveway', amount: '$420' },
  { item: 'Truck and plow cost per visit: fuel, blade wear, maintenance share', amount: '$110' },
  { item: 'Walk and step shovelling, per visit, added up for the season', amount: '$180' },
  { item: 'Ice melt for the expected number of icing events', amount: '$70' },
  { item: 'Overhead and the margin you choose, including a cushion for a heavier winter', amount: '$220' },
];

const faq = [
  {
    q: 'How do I estimate how many times I will plow in a season?',
    a: 'Look at your own records from past winters, or at local historical snowfall from a weather service, and count the storms that met your trigger depth. Use an average season, then decide how much heavier-than-average risk you want to carry in the price.',
  },
  {
    q: 'Is seasonal pricing or per-push pricing better?',
    a: 'Neither is better for everyone. Seasonal pricing gives you predictable income and the customer a fixed bill, but you carry the risk of a heavy winter. Per-push pricing follows the weather, which is fair but unpredictable. Many operators offer both, or a seasonal price with a cap on included visits.',
  },
  {
    q: 'How do I price commercial lots?',
    a: 'By area and by how the lot is used: open space for piling snow, curbs and islands to work around, and the hours the business needs it clear. Commercial work often also needs salting on its own schedule and a faster response time, which should be priced in.',
  },
  {
    q: 'Should salt be included in the price?',
    a: 'It can be, but ice melt is a real cost each time it is spread. Either price it per application or include a set number of applications and charge for more.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-snow-removal',
  path: '/resources/how-to-price-snow-removal',
  primaryKeyword: 'how to price snow removal',
  secondaryKeywords: ['snow plowing pricing', 'seasonal snow removal contract price', 'how much to charge for snow plowing'],
  title: 'How to Price Snow Removal: Seasonal and Per Push | Scopie', // 56
  metaDescription:
    'Price snow removal from expected storms, minutes per visit, truck costs, salt and margin, then sell it as a seasonal or per-push contract.',
  h1: 'How to price snow removal',
  answerFirst:
    'To price snow removal, estimate how many plowable storms an average winter brings, how long each visit takes for this property, and what each visit costs in truck time, fuel, labor and salt. Add overhead and margin, then decide whether to sell it as a season, per push, or a season with a visit cap.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  blocks: [
    {
      type: 'callout',
      id: 'risk',
      text: 'The real question in snow pricing is who carries the weather risk. A seasonal price puts it on you; per-push puts it on the customer.',
    },
    {
      type: 'list',
      id: 'inputs',
      h2: 'What you need before you can price a driveway',
      style: 'checklist',
      items: [
        { text: 'Your trigger depth: how much snow before you go out.' },
        { text: 'The number of storms that met that depth in an average season where you work.' },
        { text: 'Minutes per visit for this property, including walks and steps if they are included.' },
        { text: 'Your truck and plow cost per hour: fuel, maintenance, insurance, depreciation.' },
        { text: 'Labor cost per hour for drivers and shovellers, including overtime on storm nights.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'Worked example: a seasonal residential price',
      label: 'Building a season price for one driveway (internal costing)',
      lines: example,
      total: '$1,000',
      totalLabel: 'Seasonal price to offer',
      note: 'Illustrative only. Plug in your own storm count, minutes and costs.',
    },
    {
      type: 'text',
      id: 'models',
      h2: 'Choosing a model: season, per push, or capped season',
      paragraphs: [
        'A straight seasonal contract is simplest to sell and to bill. It rewards you in a light winter and punishes you in a heavy one, so price in a cushion. Per-push pricing is the mirror image: you are paid for every storm, but your income swings and customers sometimes delay calling you to save money.',
        'A capped seasonal contract is a middle path: one price covers up to a set number of visits, and visits beyond that are billed per push. It keeps the predictability most customers want while protecting you from a record snowfall.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Snow pricing mistakes',
      style: 'mistakes',
      items: [
        { title: 'Pricing from last winter alone', text: 'One unusual year is a poor guide. Use an average across several seasons.' },
        { title: 'Ignoring storm-night labor', text: 'Overtime, standby pay and long shifts cost more than a normal day.' },
        { title: 'Unlimited salting', text: 'Each application costs material and a visit. Count it.' },
        { title: 'Taking more accounts than the route can clear', text: 'A promised response time you cannot meet costs customers next year.' },
      ],
    },
    {
      type: 'text',
      id: 'contract',
      h2: 'From price to signed contract',
      paragraphs: [
        'In Scopie, write the season as one quote with plowing, shovelling and salting as lines, and roof raking or extra salting as optional lines. Your trigger depth, season dates and payment timing come from your saved clauses. Scopie emails the contract to the customer; they tick extras and sign, ideally in October, long before the first storm.',
      ],
      links: [{ phrase: 'write the season as one quote', href: '/for/snow-removal' }],
    },
    { type: 'faq', id: 'faq', h2: 'Snow removal pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/snow-removal', anchor: 'Scopie for snow removal', status: 'live' },
    { href: '/for/lawn-care', anchor: 'Scopie for lawn care', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
  ],
  cta: {
    heading: 'Sell next winter\'s contracts this month',
    text: 'Put your season prices, trigger depth and extras into Scopie and email every contract before the first storm.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
